import {
  Checkbox,
  ComboboxItem,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconPlus, IconTrash } from "@tabler/icons-react"
import { FieldConstraint } from "@tfg/db/browser"
import {
  Button,
  ActionIcon,
  Select,
  MultiSelect,
  AutoCompleteWithQuery,
} from "@tfg/ui"
import { TFunction } from "i18next"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { forwardRef } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import {
  CreateProductionSiteFieldDefinition,
  CreateProductionSiteFieldDefinitionSchema,
} from "@tfg/types/schemas/modules/traceability/queries/create-production-site-field-definition.dto"
import { PaginationType } from "@tfg/types/schemas/utils"

import { queryClient } from "~/backend/queryClient.ts"
import {
  useCreateTextContentMutation,
  useListTextContentsQuery,
} from "~/pages/Admin/MasterLists/CustomFields/hooks.ts"
import { useCreateProductionSiteFieldDefinitionMutation } from "~/pages/Admin/MasterLists/ProductionSiteFieldDefinitions/hooks/useCreateProductionSiteFieldDefinitionMutation"

interface Props {
  organizationId: string
  close: () => void
  activityNames: string[]
}

export const ProductionSiteFieldDefinitionCreationForm = ({
  organizationId,
  close,
  activityNames: activities,
}: Props) => {
  const { t } = useTranslation("admin")

  const onSuccess = () => {
    form.reset()
    close()
  }
  const { mutate, isPending: isLoading } =
    useCreateProductionSiteFieldDefinitionMutation(organizationId, {
      onSuccess,
    })

  const { mutate: updateTextContent, isPending: isLoadingTextContent } =
    useCreateTextContentMutation({
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: ["textContents"],
        })
        form.setFieldValue("title", data.originalText)
      },
    })

  const form = useForm<CreateProductionSiteFieldDefinition>({
    validate: zod4Resolver(CreateProductionSiteFieldDefinitionSchema),
    initialValues: {
      forActivities: [],
      title: "",
      type: "STRING",
      enumValues: [],
      enabledForClients: [organizationId],
    },
  })

  const parser = CreateProductionSiteFieldDefinitionSchema.safeParse(
    form.values
  )
  const errors = parser.success ? undefined : z.flattenError(parser.error)
  return (
    <>
      <form onSubmit={form.onSubmit(() => mutate(form.values))}>
        <Stack>
          <Checkbox
            label={t("prodSiteFieldDefs.labels.allActivities")}
            checked={form.values.forActivities.length === activities.length}
            onChange={(value) => {
              if (value.target.checked) {
                form.setFieldValue("forActivities", activities)
              } else {
                form.setFieldValue("forActivities", [])
              }
            }}
          />
          <MultiSelect
            withAsterisk
            data={activities.map((activity) => ({
              value: activity,
              label: activity,
            }))}
            label={t("prodSiteFieldDefs.labels.activities")}
            placeholder={t("prodSiteFieldDefs.placeholders.activities")}
            {...form.getInputProps("forActivities")}
            error={
              errors?.fieldErrors?.forActivities
                ? errors.fieldErrors.forActivities[0]
                : undefined
            }
          />
          <AutoCompleteWithQuery<AdminResponses.TextContent, PaginationType>
            value={form.values.title}
            label={t("prodSiteFieldDefs.labels.orginalText")}
            placeholder={t("prodSiteFieldDefs.placeholders.orginalText")}
            onCreate={(query: string) => {
              updateTextContent({ originalText: query })
              form.setFieldValue("title", query)
              return null
            }}
            clearable
            isLoading={isLoadingTextContent}
            parseOptionFunction={(data) =>
              data?.map((item) => ({
                label: item.originalText,
                value: item.originalText,
              })) || []
            }
            query={useListTextContentsQuery}
            {...form.getInputProps("title")}
            error={
              errors?.fieldErrors?.title
                ? errors.fieldErrors.title[0]
                : undefined
            }
          />
          <Select
            withAsterisk
            data={Object.values(FieldConstraint).map((value) => ({
              value: value as string,
              label: customFieldConstaintTranslations(value, t),
            }))}
            label={t("prodSiteFieldDefs.labels.type")}
            placeholder={t("prodSiteFieldDefs.placeholders.type")}
            value={form.values.type}
            onChange={(value) => {
              form.getInputProps("type").onChange(value)
              if (
                form.values.type !== "SELECT" &&
                form.values.type !== "MULTI_SELECT"
              ) {
                form.setFieldValue("enumValues", [])
              }
              form.validateField("type")
            }}
            error={
              errors?.fieldErrors?.type ? errors.fieldErrors.type[0] : undefined
            }
          />

          {(form.values.type === "SELECT" ||
            form.values.type === "MULTI_SELECT") && (
            <Stack>
              <Title order={5}>{t("customFields.enum_values")}</Title>
              <Group>
                {form.values.enumValues.map((value: string, index: number) => (
                  <AutoCompleteWithQuery<
                    AdminResponses.TextContent,
                    PaginationType
                  >
                    key={index}
                    withAsterisk
                    label={t("prodSiteFieldDefs.labels.orginalText")}
                    placeholder={t(
                      "prodSiteFieldDefs.placeholders.orginalText"
                    )}
                    query={useListTextContentsQuery}
                    parseOptionFunction={(data) =>
                      data?.map((item) => ({
                        label: item.originalText,
                        value: item.originalText,
                      })) || []
                    }
                    value={value}
                    rightSection={
                      <ActionIcon
                        icon={<IconTrash />}
                        onClick={() => {
                          const newEnumValues = [...form.values.enumValues]
                          newEnumValues.splice(index, 1)
                          form.setFieldValue("enumValues", newEnumValues)
                        }}
                      />
                    }
                    onChange={(value) => {
                      const newEnumValues = [...form.values.enumValues]
                      newEnumValues[index] = value ?? ""
                      form.setFieldValue("enumValues", newEnumValues)
                    }}
                    onCreate={(query) => {
                      const newEnumValues = [...form.values.enumValues]
                      newEnumValues[index] = query ?? ""
                      updateTextContent({ originalText: query })
                      form.setFieldValue("enumValues", newEnumValues)
                      return null
                    }}
                    error={
                      errors?.fieldErrors?.title
                        ? errors.fieldErrors.title[0]
                        : undefined
                    }
                  />
                ))}
              </Group>
              <Button
                icon={<IconPlus />}
                w={200}
                onClick={() => {
                  form.setValues({
                    ...form.values,
                    enumValues: [...form.values.enumValues, ""],
                  })
                }}
              >
                {t("customFields.add_enum_value")}
              </Button>
            </Stack>
          )}

          {form.values.type === "NUMBER" && (
            <Group>
              <TextInput
                label={t("customFields.labels.min")}
                placeholder={t("customFields.placeholders.min")}
                value={form.values.min === undefined ? "" : form.values.min}
                onChange={(event) => {
                  if (event.currentTarget.value === "") {
                    form.setFieldValue("min", undefined)
                  } else {
                    form.getInputProps("min").onChange(event)
                  }
                }}
              />
              <TextInput
                label={t("customFields.labels.max")}
                placeholder={t("customFields.placeholders.max")}
                value={form.values.max === undefined ? "" : form.values.max}
                onChange={(event) => {
                  if (event.currentTarget.value === "") {
                    form.setFieldValue("max", undefined)
                  } else {
                    form.getInputProps("max").onChange(event)
                  }
                }}
              />
            </Group>
          )}
          <Button disabled={!form.isValid()} type="submit" loading={isLoading}>
            {t("customFields.submit")}
          </Button>
        </Stack>
      </form>
    </>
  )
}

type TextContentItemProps = Omit<ComboboxItem, "ref"> &
  React.ComponentPropsWithoutRef<"div">

export const TextContentSelectItem = forwardRef<
  HTMLDivElement,
  TextContentItemProps
>(({ ...rest }: TextContentItemProps, ref) => (
  <Stack ref={ref} p="xs" gap={0} {...rest}>
    <Text fz="sm">{rest.label}</Text>
  </Stack>
))

export const customFieldConstaintTranslations = (
  constraint: FieldConstraint,
  t: TFunction<"admin", undefined>
) => {
  switch (constraint) {
    case "STRING":
      return t("customFields.constraints.string")
    case "SELECT":
      return t("customFields.constraints.enum")
    case "NUMBER":
      return t("customFields.constraints.number")
    case "MULTI_SELECT":
      return t("customFields.constraints.multi_enum")
    default:
      return constraint
  }
}
